import { FunctionComponent } from 'preact';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'preact/hooks';
import { useStyles } from '../../../../../common/hooks/useStyles';
import { playerSettingsSelector } from '../../../../../common/store/selectors/playerSettings.selector';
import { setUserInteraction } from '../../../store/controlsPlugin.actions';
import { Icon } from '../../../../../common/components/icon/icon';
import { CONTROLS_ICON_TYPES } from '../../../icon/iconMapper';
import { playerDataSelector } from '../../../../../common/store/selectors/playerData.selector';
import { adsDataSelector } from '../../../../../common/store/selectors/adsData.selector';
import { getMuteId } from '../../../htmlElements/htmlElementsIdGetter';
import { isDesktopMode, isEmpty } from '../../../../../common/utils/utils';
import { getVolumeIconStyleObject } from './volumeIcon.style';
import { recirculationVideoDataSelector } from '../../../../../common/store/selectors/recirculationVideoData.selector';
import { setAdMute, changeAdVolume } from '../../../../../core/store/monetization/monetizationSlice';
import { setMute, setVolume as changeContentVolume, setShowControls } from '../../../../../core/store/playerData/playerDataSlice';

const ARIA_LABEL = 'Volume';

interface VolumeIconComponentProps {
  isHoveredVolumeContainer: boolean;
  volumeIconHovered: boolean;
  volumeProgressBar: HTMLDivElement;
  setShowVolumeBar: (showVolumeBar: boolean) => void;
  outsideControlsBar?: boolean;
}

export const VolumeIcon: FunctionComponent<VolumeIconComponentProps> = props => {
  const {
    isHoveredVolumeContainer,
    volumeIconHovered,
    setShowVolumeBar,
    volumeProgressBar,
    outsideControlsBar,
  } = props;

  const dispatch = useDispatch();
  const volume = useSelector(playerSettingsSelector.volume);
  const contentMuted = useSelector(playerSettingsSelector.muted);
  const adMuted = useSelector(adsDataSelector.adMuted);
  const playerMode = useSelector(playerDataSelector.playerMode);
  const isVastAd = useSelector(adsDataSelector.isVastAd);
  const adBreak = useSelector(adsDataSelector.adBreak);
  const playerSize = useSelector(playerDataSelector.playerSize);
  const playerUniqueId = useSelector(playerDataSelector.playerInstanceUniqueId);
  const numberOfScenes = useSelector(recirculationVideoDataSelector.numberOfScenes);

  const showAdControls = isVastAd && adBreak;
  const desktopMode = isDesktopMode(playerMode);
  const mediumOrSmallSize = playerSize === 'small' || playerSize === 'medium';
  const showClickableWrapper = !desktopMode && mediumOrSmallSize;
  const isRecirculationControls = numberOfScenes !== 0 && !showAdControls;

  const muted = showAdControls ? adMuted : contentMuted;
  const setMuteAction = showAdControls ? setAdMute : setMute;
  const changeVolume = showAdControls ? changeAdVolume : changeContentVolume;

  const styles = useStyles(getVolumeIconStyleObject, [volumeIconHovered, desktopMode, isRecirculationControls, outsideControlsBar]);
  const { volumeIconStyle, volumeIconWrapperStyle } = styles;

  useEffect(() => {
    if (!isEmpty(volumeProgressBar)) {
      volumeProgressBar.style.height = (`${volume * 100}%`);
    }
  }, [volume, volumeProgressBar]);

  const updateUserInteraction = (newMuteState: boolean): void => {
    if (!showAdControls) {
      dispatch(setUserInteraction(newMuteState ? 'mute' : 'unmute'));
    }
  };

  const onClickVolumeButton = (): void => {
    const newMuteState = !muted;
    dispatch(setMuteAction(newMuteState));
    updateUserInteraction(newMuteState);
  };

  const onMouseEnterIcon = (): void => {
    if (isHoveredVolumeContainer && desktopMode && !outsideControlsBar) {
      setShowVolumeBar(true);
    }
  };

  const getNewVolume = (keyEvent: string): number => {
    if (keyEvent === 'ArrowUp') {
      if (muted) {
        onClickVolumeButton();
      }
      return volume < 0.95 ? Math.round((volume * 100) + 5) / 100 : 1;
    }
    if (keyEvent === 'ArrowDown') {
      if (!muted && volume < 0.05) {
        onClickVolumeButton();
      }
      return volume > 0.05 ? Math.round((volume * 100) - 5) / 100 : 0;
    }
    return volume;
  };

  const onVolumeKeyDown = (eventKey: string): void => {
    const newVolume = getNewVolume(eventKey);
    if (newVolume !== volume) {
      dispatch(changeVolume(newVolume));
    }
  };

  const onVolumeButtonKeyDown = (event: any): void => {
    if (event.key === 'Enter') {
      onClickVolumeButton();
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      onVolumeKeyDown(event.key);
    }
  };

  const onVolumeFocus = (): void => {
    dispatch(setShowControls(true));
  };

  const iconProps = {
    id: getMuteId(playerUniqueId),
    style: volumeIconStyle,
    icon: muted ? CONTROLS_ICON_TYPES.VOLUME_MUTE : CONTROLS_ICON_TYPES.VOLUME_UNMUTE,
    onMouseEnter: onMouseEnterIcon,
    ariaLabel: ARIA_LABEL,
  };

  return showClickableWrapper
    ? (
      <div
        onClick={onClickVolumeButton}
        style={volumeIconWrapperStyle}
      >
        <Icon
          {...iconProps}
        />
      </div>
    ) : (
      <Icon
        {...iconProps}
        onClick={onClickVolumeButton}
        onKeyDown={onVolumeButtonKeyDown}
        onControlFocus={onVolumeFocus}
      />
    );
};
